import { useMemo } from "react";
import { useAccessManagement } from "@visioni/a3s-access/app";
import { useIdentity } from "@visioni/a3s-dir/app";
import { useAuthReady } from "@src/features/auth/auth-store";
import { authenticatedFetch } from "@src/features/auth";
import { useEnvironment } from "@src/config/environment-store";
import { directory } from "@src/config/runtime";

/**
 * Single source of truth for the Users & Permissions surface. Fires the
 * two queries it cares about — access management (users + roles + camera
 * permissions) and caller identity — in parallel and exposes a combined
 * `isReady` flag so screens can block their rendering until both have
 * resolved, avoiding the half-hydrated state where destructive controls
 * render for a split second before the caller is recognised as self.
 *
 * Children of screens that wait for `isReady` get a warm cache for free
 * — no extra network on navigation.
 */
export function useUsersAndPermissionsData() {
  const env = useEnvironment();
  const authReady = useAuthReady();

  const accessQuery = useAccessManagement();
  const identityQuery = useIdentity({
    authenticatedFetch,
    directoryBaseUrl: directory(env),
    enabled: authReady,
  });

  const isReady = accessQuery.isSuccess && identityQuery.isSuccess;
  const isError = accessQuery.isError || identityQuery.isError;

  return useMemo(
    () => ({
      isReady,
      isError,
      accessData: accessQuery.data,
      identity: identityQuery.data,
    }),
    [isReady, isError, accessQuery.data, identityQuery.data],
  );
}
